import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { DocumentDef, FilterDef } from '@project/contracts'
import { documentListCatalogue, filterParamKeys } from '@project/contracts'
import { Button, Empty, Icon, Table } from '../design/index.js'
import { useApi, type ListRow } from '../app/api.js'
import { navigate, paths, type Query } from '../app/router.js'
import { useDocumentByRoute } from './queries.js'
import { toTableColumns } from './cells.js'
import { FilterFields, type FilterValues } from './filter-fields.js'
import { rangeLabel } from './format.js'
import { CreateWizard } from './CreateWizard.js'
import { ErrorBlock, Loading } from './feedback.js'
import { ColumnsButton, ExportButton, useChosenColumns } from './columns.js'
import { SavedViews } from './SavedViews.js'

const PAGE_SIZE = 50

/**
 * The filters as the server, the address bar and a saved view all spell them.
 *
 * One spelling for all three. A saved view used to store the drawer's own keys
 * (`documentStatus`, `documentDate:from`) while the address bar and the server
 * used `status` and `from` — so a view lost its dates when saved and its status
 * when opened, and showed itself as edited the moment it loaded. The spelling
 * itself is `filterParamKeys`, shared with the server (BILL-023).
 */
export const toFilterParams = (filters: readonly FilterDef[], values: FilterValues): Record<string, string> => {
  const params: Record<string, string> = {}
  for (const f of filters) {
    const keys = filterParamKeys(f)
    if (f.control === 'dateRange') {
      const from = values[`${f.key}:from`] ?? ''
      const to = values[`${f.key}:to`] ?? ''
      if (from) params[keys[0]!] = from
      if (to) params[keys[1]!] = to
      continue
    }
    const v = values[f.key] ?? ''
    if (v) params[keys[0]!] = v
  }
  return params
}

/** The list query the server understands: the filters, plus which page. */
const toParams = (filters: readonly FilterDef[], values: FilterValues, page: number): Record<string, string> => ({
  page: String(page),
  pageSize: String(PAGE_SIZE),
  ...toFilterParams(filters, values),
})


/**
 * Read the filter drawer's state back out of the URL.
 *
 * The inverse of `toParams`. Without it a bookmarked or shared link such as
 * `?status=APPROVED` showed an unfiltered list while the drawer read "Not
 * chosen" — the link lied about what it was showing.
 */
export const fromParams = (filters: readonly FilterDef[], query: Query): FilterValues => {
  const values: FilterValues = {}
  for (const f of filters) {
    const keys = filterParamKeys(f)
    if (f.control === 'dateRange') {
      if (query[keys[0]!]) values[`${f.key}:from`] = query[keys[0]!]!
      if (query[keys[1]!]) values[`${f.key}:to`] = query[keys[1]!]!
      continue
    }
    const v = query[keys[0]!]
    if (v) values[f.key] = v
  }
  return values
}

const FilterDrawer = ({
  filters,
  values,
  onChange,
  onApply,
  onClear,
}: {
  filters: readonly FilterDef[]
  values: FilterValues
  onChange: (key: string, value: string) => void
  onApply: () => void
  onClear: () => void
}) => (
  <aside className="filter-drawer" aria-label="Filters">
    <h2 className="filter-drawer__title">Filters</h2>
    <p className="helper">Narrow the list, then apply.</p>
    <FilterFields filters={filters} values={values} onChange={onChange} />
    <div className="row" style={{ marginTop: 24 }}>
      <Button kind="primary" onClick={onApply}>Apply</Button>
      <Button kind="ghost" onClick={onClear}>Clear</Button>
    </div>
  </aside>
)

export interface ListPageProps {
  readonly companyId: string
  readonly docRoute: string
  readonly creating: boolean
  readonly query: Query
}

export const ListPage = ({ companyId, docRoute, creating, query }: ListPageProps) => {
  const api = useApi()
  const resolved = useDocumentByRoute(docRoute)
  const def: DocumentDef | undefined = resolved.def

  // Seeded from the route so a shared link shows what it claims to show, and
  // the drawer's controls agree with the list beside them.
  // Everything the list may filter by and show — the type's own, and the
  // audit and header columns every list has (INV-016).
  const catalogue = useMemo(() => (def ? documentListCatalogue(def) : null), [def])
  const filters = catalogue?.filters ?? []
  const fromUrl = useMemo(() => (catalogue ? fromParams(catalogue.filters, query) : {}), [catalogue, query])
  const [showFilters, setShowFilters] = useState(() => Object.keys(fromUrl).length > 0)
  const [draft, setDraft] = useState<FilterValues>(fromUrl)
  const [applied, setApplied] = useState<FilterValues>(fromUrl)
  const [page, setPage] = useState(1)

  // A later navigation to the same page with a different query must re-seed.
  const urlKey = JSON.stringify(fromUrl)
  useEffect(() => {
    setDraft(fromUrl)
    setApplied(fromUrl)
    setPage(1)
  }, [urlKey])

  const params = useMemo(() => (catalogue ? toParams(catalogue.filters, applied, page) : {}), [catalogue, applied, page])

  const list = useQuery({
    queryKey: ['list', api.companyId, def?.key, params],
    queryFn: () => api.list(def?.key ?? '', params),
    enabled: Boolean(def?.key),
    placeholderData: (prev) => prev,
  })

  const chooser = useChosenColumns(def ? `list:${def.key}` : '', catalogue?.columns ?? [], def?.columns ?? [])
  const columns = useMemo(() => toTableColumns<ListRow>(chooser.visible), [chooser.visible])

  if (resolved.missing) {
    return (
      <div className="page">
        <h1 className="page-title">Page not found</h1>
        <p className="page-subtitle">There is no document type at “{docRoute}”.</p>
      </div>
    )
  }
  if (resolved.error) return <div className="page"><ErrorBlock error={resolved.error} /></div>
  if (!def) return <div className="page"><Loading /></div>

  const rows = list.data?.rows ?? []
  const total = list.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const plural = def.labelPlural.toLowerCase()

  return (
    <div className="page">
      <h1 className="page-title">{def.labelPlural}</h1>

      <div className="list-toolbar">
        <Button kind="primary" icon={<Icon name="plus" size={18} />} onClick={() => navigate(paths.create(companyId, docRoute))}>
          Create {def.label.toLowerCase()}
        </Button>
        <Button
          kind="ghost"
          icon={<Icon name="filter" size={18} />}
          aria-expanded={showFilters}
          onClick={() => setShowFilters((v) => !v)}
        >
          {showFilters ? 'Hide filters' : 'Show filters'}
        </Button>
        <SavedViews
          scope={`doc:${def.key}`}
          filters={toFilterParams(filters, applied)}
          columns={chooser.chosen}
          editor={{
            columns: catalogue?.columns ?? def.columns,
            currentColumns: chooser.visible.map((c) => c.key),
            filters,
            currentFilters: applied,
            toParams: (values) => toFilterParams(filters, values),
            fromParams: (stored) => fromParams(filters, stored),
          }}
          onApply={(view) => {
            const values = fromParams(filters, view.filters)
            setDraft(values)
            setApplied(values)
            setPage(1)
            if (view.columns.length > 0) chooser.choose(view.columns)
            navigate(paths.list(companyId, docRoute, view.filters))
          }}
          onDefault={(view) => {
            // Only when the address bar asked for nothing: a shared link wins
            // over someone's own default, or the link does not mean anything.
            if (Object.keys(query).length > 0) return
            const values = fromParams(filters, view.filters)
            setDraft(values)
            setApplied(values)
            if (view.columns.length > 0) chooser.choose(view.columns)
          }}
        />
        <ColumnsButton all={catalogue?.columns ?? def.columns} state={chooser} title={`Columns on ${plural}`} />
        <ExportButton
          href={api.exportUrl('docs', def.key, params, chooser.chosen)}
          disabled={total === 0}
          count={total}
          what={plural.toLowerCase()}
        />
        <span className="masthead__spacer" />
        <span className="list-count">{list.isLoading ? 'Loading…' : rangeLabel(page, PAGE_SIZE, total)}</span>
      </div>

      {list.error ? <ErrorBlock error={list.error} /> : null}

      <div className="list-layout">
        <div className="list-layout__main">
          {list.isLoading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Empty
              title={`No ${plural}!`}
              body={[
                `You don't have any ${plural} in this view.`,
                'Check the filter conditions.',
                `Otherwise you may not have access to all ${plural}.`,
              ]}
              actions={
                <Button kind="primary" onClick={() => navigate(paths.create(companyId, docRoute))}>
                  Create {def.label.toLowerCase()}
                </Button>
              }
            />
          ) : (
            <>
              <Table
                columns={columns}
                rows={rows}
                getKey={(row) => row.id}
                onRowClick={(row) => navigate(paths.document(companyId, docRoute, row.id))}
              />
              <div className="pager">
                <Button kind="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <span className="list-count">Page {page} of {pageCount}</span>
                <Button kind="ghost" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                  Next
                </Button>
              </div>
            </>
          )}
        </div>

        {showFilters ? (
          <FilterDrawer
            filters={filters}
            values={draft}
            onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))}
            onApply={() => {
              setApplied(draft)
              setPage(1)
            }}
            onClear={() => {
              setDraft({})
              setApplied({})
              setPage(1)
            }}
          />
        ) : null}
      </div>

      {creating ? (
        <CreateWizard
          def={def}
          companyId={companyId}
          docRoute={docRoute}
          seed={query}
          onClose={() => navigate(paths.list(companyId, docRoute))}
        />
      ) : null}
    </div>
  )
}
